// Path: ./src/app/components/SideFlyerGridList/index.tsx
import * as React from "react";
import { ImageHoverTitleWrapper } from "../ImageHoverTitle";
import InfiniteScroll from "react-infinite-scroll-component";
import {
  Backdrop,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip,
} from "@mui/material";
import { generateUniqueNumber } from "../../../utils/generateUniqueNumber";
import AddIcon from "@mui/icons-material/Add";
import {
  GfImage,
  GfImageWithUsageCount,
} from "@swagger/typescript-fetch-goflyer";
import { useSortedGfImageResults } from "../../../hooks/useSortedGfImageResults";
import {
  ImageWrapper,
  Title,
  ImageIcon,
  NoImageFoundWrapper,
  HelperTextContainer,
  HelperText,
  Item,
  GridContainer,
  Wrapper,
} from "./styled";
import { ButtonComponent } from "../Button";
import { ButtonWithTooltip } from "../ButtonWithTooltip";
import { useTranslation } from "react-i18next";
import { messages } from "../../../locales/messages";
import StraightIcon from "@mui/icons-material/Straight";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import { ImagePreviewModal } from "../ImagePreviewModal";
import { formatDateToDayMonthYear, formatFileSize } from "../../../utils/utils";
import {
  DateFormat,
  S3Size,
  UsageIndicator,
  UsageCountBadge,
} from "../../../utils/commonStyledComponents/commonStyled";
import { LazyImage } from "../LazyImage";
import { PermissionComponentWrapper } from "../PermissionComponentWrapper";
import { ImageTagsOverlay } from "../ImageTagsOverlay";
import { useShowImageTagsInSearch } from "../../../hooks/useShowImageTagsInSearch";

interface Props {
  itemsList?: GfImageWithUsageCount[];
  /**
   * Click event handler for selecting a flyer image when it's clicked.
   * Invoked when a flyer image is clicked in order to select it.
   * @param value - The value associated with the selected flyer image object.
   */
  // eslint-disable-next-line
  onItemClick?: (value: GfImageWithUsageCount) => void;
  gridStyle?: string;
  dataTestId?: string;
  hasMore: boolean;
  getMore: () => void;
  isLoading: boolean;
  searchText?: string;
  shouldRenderCreateFlyerButton?: boolean;
  /**
   * File upload handler for selecting local images
   */
  onFileUpload?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * Custom styles for the container wrapper
   */
  containerStyle?: React.CSSProperties;
  // eslint-disable-next-line
  onTagEdit?: (image: GfImage) => void;
}

/**
 * product image list on the right side when there is already existing flyer item
 * @param param0
 * @returns
 */
export const SideFlyerGridList = React.memo(
  function SideFlyerGridList({
    itemsList,
    onItemClick,
    dataTestId,
    hasMore = false,
    getMore,
    isLoading = true,
    searchText = ``,
    shouldRenderCreateFlyerButton = false,
    onFileUpload,
    containerStyle,
    onTagEdit,
  }: Props) {
    const scrollId = `inFiniteScroll-${generateUniqueNumber()}`;
    const { t } = useTranslation();
    const showImageTags = useShowImageTagsInSearch();
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const sortedItemsList = useSortedGfImageResults({
      itemsList,
      searchText,
    });

    return (
      <Wrapper
        style={{
          background: "#eeeeee",
          boxShadow: "inset 0 0 10px #b7b7b7a1",
          ...containerStyle,
        }}
        data-testid={dataTestId}
        id={scrollId}
      >
        <InfiniteScroll
          // The 'searchText' is used to manage the infinite scroll behavior.
          // When 'searchText' changes, it will destroy the old instance of infinite scroll
          // and initialize a new one to reflect the new search criteria.
          key={searchText || `inFiniteKey-${generateUniqueNumber()}`}
          scrollThreshold={0.8}
          next={getMore}
          dataLength={sortedItemsList?.length ? sortedItemsList.length : 0}
          hasMore={hasMore}
          scrollableTarget={scrollId}
          loader={<></>}
          style={{ height: "100%", background: "transparent" }}
        >
          <GridContainer
            container
            columns={{ xs: 2, sm: 4, md: 3, lg: 3, xl: 4 }}
            rowSpacing={"10px"}
            data-testid={dataTestId + "-listWrapper"}
            height={"100%"}
          >
            {sortedItemsList?.map((value, index) => {
              return (
                <Grid
                  item
                  xs={1}
                  sm={1}
                  md={1}
                  key={index}
                  data-testid="sideflyergridlist-test-id"
                >
                  <ImagePreviewModal src={value.src}>
                    <Item
                      onClick={() => {
                        onItemClick && onItemClick(value);
                      }}
                      data-testid={`${dataTestId}-image`}
                    >
                      <ImageHoverTitleWrapper text={value.alt} delay={500}>
                        <ImageWrapper
                          hasUsageHistory={
                            (value?.flyerItemUsageCount || 0) > 0
                          }
                        >
                          {(value?.flyerItemUsageCount || 0) > 0 && (
                            <UsageIndicator />
                          )}
                          <ImageIcon>
                            <AddIcon style={{ fontSize: 15 }} />
                          </ImageIcon>
                          <LazyImage
                            src={value?.srcSmall || value?.src}
                            alt={value.alt}
                            enableFadeIn={true}
                            rootMargin="100px"
                            threshold={0.1}
                            data-testid={`${dataTestId}-lazy-image`}
                          />
                          {showImageTags && (
                            <ImageTagsOverlay
                              imageTags={value.imageTags}
                              dataTestId={`${dataTestId}-image-tags`}
                            />
                          )}
                          <DateFormat data-testid={`${dataTestId}-date`}>
                            {formatDateToDayMonthYear(value?.updatedAt)}
                          </DateFormat>
                          {value?.s3Size && (
                            <S3Size data-testid={`${dataTestId}-size`}>
                              {formatFileSize(value?.s3Size)}
                            </S3Size>
                          )}
                          {(value?.flyerItemUsageCount || 0) > 0 && (
                            <UsageCountBadge
                              count={value?.flyerItemUsageCount || 0}
                              data-testid={`${dataTestId}-usage-count`}
                              title={`Used in ${
                                value?.flyerItemUsageCount || 0
                              } flyer item${
                                (value?.flyerItemUsageCount || 0) !== 1
                                  ? "s"
                                  : ""
                              }`}
                            >
                              {value?.flyerItemUsageCount}
                            </UsageCountBadge>
                          )}
                          {onTagEdit && (
                            <PermissionComponentWrapper
                              requiredPermission={["update"]}
                              requiredPermissionGroup={["imageTag"]}
                              wrapperCss={`
                              display:block;
                              margin-bottom:5px;
                              margin-top: 5px;
                              `}
                            >
                              <div
                                className="tag-edit-btn"
                                style={{
                                  position: "absolute",
                                  bottom: 4,
                                  right: 4,
                                  zIndex: 3,
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  onTagEdit(value);
                                }}
                              >
                                <Tooltip
                                  title="Edit Image Tags"
                                  placement="top"
                                >
                                  <IconButton
                                    size="small"
                                    sx={{
                                      bgcolor: "rgba(255,255,255,0.9)",
                                      padding: "3px",
                                      "&:hover": { bgcolor: "white" },
                                    }}
                                  >
                                    <LocalOfferIcon
                                      sx={{ fontSize: 14, color: "#f58220" }}
                                    />
                                  </IconButton>
                                </Tooltip>
                              </div>
                            </PermissionComponentWrapper>
                          )}
                        </ImageWrapper>
                        <Title data-testid={`${dataTestId}-title`}>
                          {value.alt}
                        </Title>
                      </ImageHoverTitleWrapper>
                    </Item>
                  </ImagePreviewModal>
                </Grid>
              );
            })}
            {!sortedItemsList?.length &&
            searchText?.length > 0 &&
            !isLoading ? (
              <NoImageFoundWrapper>
                <HelperTextContainer>
                  {onFileUpload ? (
                    <>
                      <input
                        type="file"
                        id="upload-image-search"
                        accept="image/*,.psd"
                        multiple={true}
                        hidden
                        ref={fileInputRef}
                        onChange={(e) => onFileUpload(e)}
                        data-testid="upload-image-input"
                      />
                      <ButtonWithTooltip
                        showTooltip
                        tooltipText={t(messages.UPLOAD_IMAGES_GUIDE())}
                        tooltipPlacement="top"
                        variant="outline"
                        size="medium"
                        data-testid="upload-image-button"
                        onClick={async () => {
                          if (fileInputRef.current) {
                            fileInputRef.current.click();
                          }
                        }}
                      >
                        {t(messages.UPLOAD_IMAGE())}
                      </ButtonWithTooltip>
                    </>
                  ) : (
                    <h1>{t(messages.NO_IMAGE_FOUND())}</h1>
                  )}
                  {shouldRenderCreateFlyerButton ? (
                    <ButtonComponent
                      title={`${t(messages.Create_Flyer_Item())}`}
                      testId="create-flyer-item-button"
                      onClick={async () => {
                        const date = new Date();
                        onItemClick &&
                          onItemClick({
                            id: "",
                            src: "",
                            createdAt: date,
                            updatedAt: date,
                            isDeleted: false,
                          });
                      }}
                    ></ButtonComponent>
                  ) : (
                    <></>
                  )}
                </HelperTextContainer>
              </NoImageFoundWrapper>
            ) : shouldRenderCreateFlyerButton &&
              !sortedItemsList?.length &&
              searchText?.length === 0 &&
              !isLoading ? (
              <NoImageFoundWrapper>
                <HelperTextContainer>
                  <StraightIcon style={{ fontSize: "150px" }} />
                  <HelperText>
                    {t(messages.SEARCH_SELECT_CREATE_FLYER())}
                    <br />
                    {t(messages.CREATE_FLYER_OWN_IMAGE())}
                  </HelperText>
                  <ButtonComponent
                    title={`${t(messages.Create_Flyer_Item())}`}
                    testId="create-flyer-item-button"
                    onClick={async () => {
                      const date = new Date();
                      onItemClick &&
                        onItemClick({
                          id: "",
                          src: "",
                          createdAt: date,
                          updatedAt: date,
                          isDeleted: false,
                        });
                    }}
                  ></ButtonComponent>
                </HelperTextContainer>
              </NoImageFoundWrapper>
            ) : (
              <></>
            )}
          </GridContainer>
        </InfiniteScroll>
        <Backdrop
          sx={{
            color: "#ff813a",
            zIndex: 9,
            position: "absolute",
            backgroundColor: "rgb(0 0 0 / 18%)",
          }}
          open={isLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Wrapper>
    );
  },
  (prevProps, nextProps) => {
    if (
      prevProps.hasMore !== nextProps.hasMore ||
      prevProps.isLoading !== nextProps.isLoading ||
      prevProps.searchText !== nextProps.searchText ||
      prevProps.shouldRenderCreateFlyerButton !==
        nextProps.shouldRenderCreateFlyerButton ||
      prevProps.dataTestId !== nextProps.dataTestId
    ) {
      return false;
    }

    const prevList = prevProps.itemsList;
    const nextList = nextProps.itemsList;

    if (prevList === nextList) return true;
    if (!prevList && !nextList) return true;
    if (!prevList || !nextList) return false;
    if (prevList.length !== nextList.length) return false;

    if (prevList.length > 0) {
      if (prevList[0]?.id !== nextList[0]?.id) return false;
      if (
        prevList[prevList.length - 1]?.id !== nextList[nextList.length - 1]?.id
      )
        return false;
    }

    if (prevProps.onItemClick !== nextProps.onItemClick) return false;
    if (prevProps.getMore !== nextProps.getMore) return false;
    if (prevProps.onFileUpload !== nextProps.onFileUpload) return false;
    if (prevProps.onTagEdit !== nextProps.onTagEdit) return false;

    if (
      JSON.stringify(prevProps.containerStyle) !==
      JSON.stringify(nextProps.containerStyle)
    )
      return false;

    return true;
  },
);
